import { api } from "@clarent/backend/convex/_generated/api";
import type { Id } from "@clarent/backend/convex/_generated/dataModel";
import { hasStartedStatus, planItems, stepHours, type PlanItem } from "@clarent/scheduling";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { JobRow } from "../routes/BoardPage";
import { errorMessage } from "../lib/convexError";
import { formatDateLong, formatDay, formatHours, jobTitle, plural } from "../lib/format";
import { useMachines } from "../lib/machines";
import { formatRange, planItemDetail, planItemTitle, workingDaysFromToday } from "../lib/plan";
import { PROCESS_META, processKind } from "../lib/process";
import { nextStage, RISK_META, STATUS_META } from "../lib/risk";
import { CloseIcon, MoreIcon } from "./icons";

/**
 * Job drawer from design system v2.1: 500px, docked right on the scrim. Header, date tiles,
 * the plan counted back from the due date, details, and a footer whose primary action moves
 * the job to its usual next stage. `row` is the board's row for this job; it is missing while
 * the board loads and for archived jobs, which aren't scheduled.
 */
export function JobDrawer({ jobId, row, onClose }: { jobId: Id<"jobs">; row?: JobRow; onClose: () => void }) {
  const job = useQuery(api.jobs.get, { jobId });
  const settings = useQuery(api.settings.get);
  const machines = useMachines();
  const setStatus = useMutation(api.jobs.setStatus);
  const archive = useMutation(api.jobs.archive);
  const restore = useMutation(api.jobs.restore);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const panel = useRef<HTMLElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && (menuOpen ? setMenuOpen(false) : onClose());
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen, onClose]);
  useEffect(() => panel.current?.focus(), []);

  const act = async (fn: () => Promise<unknown>) => {
    try {
      setError(null);
      setMenuOpen(false);
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const archived = job?.archivedAt !== undefined;
  const items = row && settings ? planItems(row.schedule, settings.workingDays) : [];
  const next = row && !archived ? nextStage(row.status, row.steps) : null;
  const started = row ? hasStartedStatus(row.status) : false;

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={job ? `Job ${job.reference}` : "Job"}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[500px] flex-col bg-bg shadow-drawer outline-none"
      >
        <header className="flex flex-col gap-3.5 border-b border-line px-7 pt-6 pb-5">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs leading-4 text-muted">{job?.reference ?? " "}</span>
            <div className="relative flex gap-2">
              {job && (
                <button type="button" className="icon-btn" aria-label="More" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
                  <MoreIcon />
                </button>
              )}
              <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
                <CloseIcon />
              </button>
              {menuOpen && job && (
                <div className="absolute top-11 right-11 z-10 w-48 rounded-md border border-line bg-bg p-1 shadow-tip" role="menu">
                  {archived ? (
                    <MenuItem onClick={() => act(() => restore({ jobId }))}>Restore job</MenuItem>
                  ) : (
                    <MenuItem onClick={() => act(() => archive({ jobId }).then(onClose))}>Archive job</MenuItem>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <h2 className="overlay-title">{job ? jobTitle(job) : "Loading job…"}</h2>
            {job?.part && <p className="text-sm leading-[18px] text-muted">{job.customerName}</p>}
          </div>
          {row && (
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-surface px-2.5 py-[5px] text-xs leading-4 font-bold">
                <span className={`size-2 rounded-[3px] ${STATUS_META[row.status].marker}`} />
                {STATUS_META[row.status].label}
              </span>
              {row.status === "complete" ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-surface px-2.5 py-[5px] text-xs leading-4 font-bold text-muted">
                  <span className="size-[7px] rounded-full bg-quoted" />
                  Done
                </span>
              ) : (
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-[5px] text-xs leading-4 font-bold ${RISK_META[row.schedule.riskLevel].pill}`}>
                  <span className={`size-[7px] rounded-full ${RISK_META[row.schedule.riskLevel].dot}`} />
                  {RISK_META[row.schedule.riskLevel].label}
                </span>
              )}
            </div>
          )}
        </header>

        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-7 py-5">
          {error && <p className="notice-error">{error}</p>}
          {archived && (
            <div className="rounded-tile bg-surface p-4 text-sm">
              <p className="font-bold">This job is archived</p>
              <p className="mt-1 text-muted">It isn't on the board or calendar and won't raise alerts. Restore it to change it.</p>
              <button type="button" className="btn-primary btn-md mt-3" onClick={() => act(() => restore({ jobId }))}>
                Restore job
              </button>
            </div>
          )}
          {row && settings && machines && (
            <>
              <div className="flex gap-2.5">
                {!started && (
                  <DateTile
                    tone={row.schedule.riskLevel === "impossible" ? "late" : "brand"}
                    label="Latest start"
                    date={formatDateLong(row.schedule.latestStartDate)}
                    note={workingDaysFromToday(row.schedule.slackWorkingDays)}
                  />
                )}
                <DateTile tone="surface" label="Due" date={formatDateLong(row.dueDate)} note="Promised to the customer" />
              </div>

              <section className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-[15px] leading-[18px] font-extrabold">The plan</h3>
                  <span className="text-xs text-muted">Counted back from the due date</span>
                </div>
                <ol>
                  {items.map((item, i) => (
                    <PlanRow key={`${item.stepIndex}-${item.kind}`} first={i === 0} last={false} item={item} machineName={machines.name} />
                  ))}
                  <TimelineRow
                    date={formatDateLong(row.dueDate)}
                    dot="bg-ink"
                    first={items.length === 0}
                    last
                    title={`Due at ${row.customerName}`}
                    detail={
                      row.schedule.effectiveDueDate === row.dueDate
                        ? "The date you promised"
                        : `Ready by ${formatDateLong(row.schedule.effectiveDueDate)}, the last working day before`
                    }
                  />
                </ol>
              </section>

              <section className="flex flex-col">
                <h3 className="pb-1.5 text-[15px] leading-[18px] font-extrabold">Details</h3>
                {row.quantity !== undefined && <DetailRow label="Quantity" value={`${row.quantity} off`} />}
                <DetailRow label="Machining" value={formatHours(row.steps.reduce((h, s) => h + (s.type === "machining" ? stepHours(s) : 0), 0))} />
                {row.steps.map((s, i) =>
                  s.type === "post_process" ? (
                    <DetailRow key={i} label="Outside process" value={`${s.name} · ${plural(s.leadDays, "calendar day")}`} />
                  ) : null,
                )}
                <DetailRow label="Job number" value={row.reference} />
                {row.completedAt !== undefined && <DetailRow label="Shipped" value={formatDay(new Date(row.completedAt))} />}
                {row.notes && <DetailRow label="Notes" value={row.notes} />}
              </section>
            </>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-line px-7 py-4">
          <span className="flex items-center gap-2 text-xs text-muted">
            <kbd className="kbd">Esc</kbd> to close
          </span>
          {job && !archived && (
            <div className="flex gap-2.5">
              <Link to="/jobs/$jobId/edit" params={{ jobId }} className="btn-secondary btn-md px-[18px] py-[11px] text-sm">
                Edit job
              </Link>
              {next && (
                <button type="button" className="btn-primary btn-md px-5 py-[11px] text-sm" onClick={() => act(() => setStatus({ jobId, status: next.status }))}>
                  {next.label}
                </button>
              )}
            </div>
          )}
        </footer>
      </aside>
    </>
  );
}

function DateTile({ tone, label, date, note }: { tone: "brand" | "late" | "surface"; label: string; date: string; note: string }) {
  const styles = {
    brand: { box: "bg-brand text-white", label: "text-brand-tint" },
    late: { box: "bg-late text-white", label: "text-white/80" },
    surface: { box: "bg-surface text-ink", label: "text-muted" },
  }[tone];
  return (
    <div className={`flex flex-1 flex-col gap-1.5 rounded-tile px-[18px] py-4 ${styles.box}`}>
      <span className={`tile-label ${styles.label}`}>{label}</span>
      <span className="tile-date">{date}</span>
      <span className={`text-xs leading-4 ${tone === "surface" ? "text-muted" : ""}`}>{note}</span>
    </div>
  );
}

function PlanRow({ item, first, last, machineName }: { item: PlanItem; first: boolean; last: boolean; machineName: (machineId: string) => string }) {
  const kind = item.kind === "process" ? processKind(item.name ?? "") : null;
  const dot = first ? "bg-brand" : item.kind === "machining" ? "bg-ink" : kind ? PROCESS_META[kind].dot : "bg-quoted";
  const chip = kind && PROCESS_META[kind].short ? <span className={`tag shrink-0 ${PROCESS_META[kind].tag}`}>{PROCESS_META[kind].short}</span> : null;
  return (
    <TimelineRow
      date={formatRange(item.start, item.end)}
      dot={dot}
      first={first}
      last={last}
      title={planItemTitle(item)}
      detail={planItemDetail(item, machineName)}
      chip={chip}
    />
  );
}

/** One row of the plan timeline: date in a 140px right-aligned column, a dot on the line, then the card. */
function TimelineRow({ date, dot, first, last, title, detail, chip }: { date: string; dot: string; first: boolean; last: boolean; title: string; detail: string; chip?: ReactNode }) {
  // Ranges may break onto two lines, but only after the dash.
  const [from, to] = date.split(" – ");
  return (
    <li className="flex gap-3">
      <div className="flex w-[140px] shrink-0 justify-end pt-[13px]">
        <span className={`text-right font-mono text-[11px] leading-[1.5] uppercase ${first ? "font-bold text-brand-dark" : "text-muted"}`}>
          {to === undefined ? (
            date
          ) : (
            <>
              <span className="whitespace-nowrap">{from} –</span> <span className="whitespace-nowrap">{to}</span>
            </>
          )}
        </span>
      </div>
      <div className="flex w-3 shrink-0 flex-col items-center" aria-hidden="true">
        <span className={`h-[18px] w-0.5 shrink-0 ${first ? "" : "bg-line"}`} />
        <span className={`size-3 shrink-0 rounded-full ${dot}`} />
        {!last && <span className="w-0.5 flex-1 bg-line" />}
      </div>
      <div className={`flex flex-1 ${last ? "" : "pb-2"}`}>
        <div className={`flex flex-1 items-center justify-between gap-2.5 rounded-md border px-3.5 py-2.5 ${first ? "border-brand bg-brand-tint" : "border-line bg-bg"}`}>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm leading-[18px] font-bold">{title}</span>
            <span className="text-xs leading-4 text-muted">{detail}</span>
          </div>
          {chip}
        </div>
      </div>
    </li>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 border-t border-line py-[9px] text-[13px] leading-4">
      <span className="text-muted">{label}</span>
      <span className="text-right font-semibold">{value}</span>
    </div>
  );
}

function MenuItem({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" role="menuitem" className="block w-full rounded-sm px-3 py-2 text-left text-sm font-medium hover:bg-surface" onClick={onClick}>
      {children}
    </button>
  );
}
